<template>
    <template v-if="!error.length">
        <a
            href="#main"
            class="skip-link"
            tabindex="0"
        >
            Skip to main content
        </a>

        <PreHeader v-if="mainMenu" />

        <AppHeader
            v-if="mainMenu"
            :main-menu
        />
    </template>
</template>
<script setup lang="ts">
import {
    computed,
    defineAsyncComponent,
    hydrateOnIdle,
    onServerPrefetch,
    watch,
} from 'vue'
import { useMenus } from '@/plugins/menu.ts'

import { useInit } from '@/plugins/init/use-init.ts'
import { useResponseError } from '@/helpers/errors'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import {
    getLocaleForRegion,
    getRoutingRegion,
    noPrefixRegion,
    useRegionPlugin,
} from '@/plugins/region.ts'
import { handleRedirection } from '@/redirect.ts'
import { useRoutes } from '@/router/routes.ts'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl.ts'
import { useRouter } from 'vue-router'
import { useWpRedirectQuery } from '@/wpRedirect.generated.ts'
import {
    getEngagementRingProps,
    isEngagementRingRoute,
} from '@/views/product/engagement-ring/er.ts'
import { toBooleanCookie, useCookie } from '@/plugins/cookie-plugin.ts'
import { useRingBuilder } from '@/plugins/ring-builder.ts'
import { getEngagementRingFilterInputFromProps } from '@/domain/product/ring/engagement.ts'
import { useRingBuilderDataStore } from '@/plugins/ring-builder-data-store.ts'
import type { RingBuilderQueryVariables } from '@/graphql/api'
import { useRingBuilderQuery } from '@/composables/app/ringBuilder.generated.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

const PreHeader = defineAsyncComponent({
    loader: () => import('@/components/layout/header/PreHeader.vue'),
    hydrate: hydrateOnIdle(),
})

const AppHeader = defineAsyncComponent({
    loader: () => import('@/components/layout/header/AppHeader.vue'),
    hydrate: hydrateOnIdle(),
})

const router = useRouter()
const routes = useRoutes()
const absoluteUrl = useAbsoluteUrl()
const redirect = useRedirect()
const { regionCode } = useRegionPlugin()

const wpRedirectResponse = useWpRedirectQuery({
    variables: computed(() => {
        const url = absoluteUrl(
            // @ts-expect-error because I CBA
            router.resolve({
                ...router.currentRoute.value,
                params: {
                    ...router.currentRoute.value.params,
                    region: getRoutingRegion(noPrefixRegion),
                },
            })
        )

        return {
            currentUrl: url.href,
        }
    }),
})

const { data: wpRedirectData } = wpRedirectResponse

watch(wpRedirectData, async (data) => {
    if (data?.redirection) {
        await handleRedirection(
            regionCode.value,
            redirect,
            router,
            routes,
            data.redirection.target,
            data.redirection.code
        )
    }
})

const { mainMenu } = useMenus()

const init = useInit()

const initialRoute = router.currentRoute.value
const isErRoute = isEngagementRingRoute(initialRoute)
const hasSession = toBooleanCookie(useCookie('SID'))
let startRbQuery = import.meta.env.SSR ? hasSession.value : true
let awaitRbQuery = import.meta.env.SSR && hasSession.value

const { addRingAndStone, markAsLoaded } = useRingBuilder()

if (isErRoute) {
    const erProps = getEngagementRingProps(initialRoute)

    if (erProps.nonSku) {
        // In theory, if ring and stone are the same, we could skip this with an if statement
        // However, keep it as-is for now, because in local environments
        // Due to the lack of a proper API proxy, cookies are on different hosts
        // And this ensures no mismatches locally when running a prod-built server
        // Otherwise, the RB state will get overwritten by the RB state query
        // And will become empty due to the lack of a cookie
        // This is because you go to thlocal.local:3000, but the API is at staging-test.coderings.com
        const addRingAndStonePromise = addRingAndStone(
            getEngagementRingFilterInputFromProps(erProps),
            erProps.nonSku
        )

        if (import.meta.env.SSR) {
            onServerPrefetch(async () => {
                await addRingAndStonePromise
            })
        } else {
            await addRingAndStonePromise
        }

        if (import.meta.env.SSR) {
            startRbQuery = false
        }

        awaitRbQuery = false
    }
}

const currency = useCurrencyInput()

if (startRbQuery) {
    const { setState: setRingBuilderState } = useRingBuilderDataStore()

    const variables = computed((): RingBuilderQueryVariables => {
        return {
            locale: getLocaleForRegion(regionCode.value),
            currency: currency.value,
            region: regionCode.value,
        }
    })

    // const client = useGraphqlClient()
    // const result = callUseQuery(
    //     {
    //         query: RingBuilderDocument,
    //         variables,
    //     },
    //     client
    // )
    const result = useRingBuilderQuery({
        variables,
    })

    const { data: rbData } = result

    if (awaitRbQuery) {
        onServerPrefetch(async () => {
            await result
            setRingBuilderState(rbData.value?.ringBuilder)

            markAsLoaded()
        })
    } else {
        markAsLoaded()
    }

    if (!import.meta.env.SSR) {
        watch(rbData, (n) => {
            setRingBuilderState(n?.ringBuilder)
        })
    }
} else {
    markAsLoaded()
}

async function awaitStuff(): Promise<void> {
    const { data: wpRedirectData } = await wpRedirectResponse
    const wpRedirect = wpRedirectData.value?.redirection

    if (wpRedirect) {
        await handleRedirection(
            regionCode.value,
            redirect,
            router,
            routes,
            wpRedirect.target,
            wpRedirect.code
        )
    }

    await init.promise
}

if (import.meta.env.SSR) {
    onServerPrefetch(async () => {
        await awaitStuff()
    })
} else {
    await init.promise
}

const { initQueryResponse } = init

const error = useResponseError(initQueryResponse)
</script>

<style lang="scss" scoped>
.skip-link {
    position: absolute;
    top: 280px;
    left: 40px;
    z-index: 1000;
    width: 120px;
    padding: 12px;
    background-color: $stone-2;

    font-family: $effra-font-stack;
    letter-spacing: 0.05em;
    font-size: 1rem;
    font-weight: 400;
    color: $blue;
    cursor: pointer;
    text-align: center;
    text-decoration: underline;

    &:not(:focus) {
        clip-path: rect(1px 1px 1px 1px);
        overflow: hidden;
        top: -1px;
        left: -1px;
        padding: 0;
    }
}
</style>
